"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import cytoscape, { type Core, type LayoutOptions } from "cytoscape";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getKnowledgeGraph, type KnowledgeGraph } from "@/lib/knowledge-api";

interface KbGraphTabProps {
  kbName: string;
}

type LayoutName = "cose" | "breadthfirst" | "concentric";

/** IMS CASE v1.1 관계 타입 → 한국어 라벨/색상. */
const RELATION_STYLE: Record<string, { label: string; color: string }> = {
  PRECEDES: { label: "Precedes", color: "#e11d48" },
  IS_PART_OF: { label: "Part of", color: "#2563eb" },
  IS_CHILD_OF: { label: "Child of", color: "#19014f" },
  IS_RELATED_TO: { label: "Related to", color: "#6b7280" },
  IS_PEER_OF: { label: "Peer of", color: "#0891b2" },
  EXEMPLAR: { label: "Exemplar", color: "#ea580c" },
  EXACT_MATCH_OF: { label: "Exact match", color: "#16a34a" },
};

function relationStyle(type: string) {
  return RELATION_STYLE[type] ?? { label: type, color: "#9ca3af" };
}

/** density 0(성김)~1(조밀) → cose 레이아웃 간격 파라미터. layoutName별 옵션 생성. */
function buildLayoutOptions(name: LayoutName, density: number): LayoutOptions {
  if (name === "breadthfirst") {
    return { name: "breadthfirst", directed: true, padding: 30, animate: false } as LayoutOptions;
  }
  if (name === "concentric") {
    return { name: "concentric", padding: 30, animate: false } as LayoutOptions;
  }
  const spread = 1 - density;
  // 중앙 허브 뭉침을 풀려면 반발력만으로는 부족하다. 연결된 노드를 벌리는
  // idealEdgeLength를 크게 키우고, 중심으로 끌어당기는 gravity를 성김일수록 낮추고,
  // 컴포넌트 간격도 벌린다.
  return {
    name: "cose",
    animate: false,
    padding: 30,
    nodeRepulsion: 12000 + spread * 900000,
    idealEdgeLength: 40 + spread * 400,
    edgeElasticity: 60 + density * 140,
    gravity: 0.12 + density * 0.88,
    componentSpacing: 80 + spread * 220,
    nodeOverlap: 20,
    numIter: 1500,
  } as LayoutOptions;
}

/** ise-kmap KB 전용 개념 그래프 뷰 — Cytoscape.js 기반. */
export default function KbGraphTab({ kbName }: KbGraphTabProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const densityRef = useRef(0.5);
  const layoutNameRef = useRef<LayoutName>("cose");
  const precedeModeRef = useRef(false);
  const [graph, setGraph] = useState<KnowledgeGraph | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{
    name: string;
    type: string;
    definition: string;
  } | null>(null);
  const [densityDisplay, setDensityDisplay] = useState(50);
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const [layoutName, setLayoutName] = useState<LayoutName>("cose");
  const [precedeMode, setPrecedeMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlight, setHighlight] = useState<{ nodes: Set<string>; edges: Set<string> } | null>(
    null,
  );

  useEffect(() => {
    layoutNameRef.current = layoutName;
  }, [layoutName]);
  useEffect(() => {
    precedeModeRef.current = precedeMode;
  }, [precedeMode]);

  useEffect(() => {
    let cancelled = false;
    setGraph(null);
    setError(null);
    setSelected(null);
    setHiddenTypes(new Set());
    setHighlight(null);
    setPrecedeMode(false);
    setSearchQuery("");
    setLayoutName("cose");
    getKnowledgeGraph(kbName)
      .then((data) => {
        if (!cancelled) setGraph(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [kbName]);

  const relationTypes = useMemo(
    () => Array.from(new Set(graph?.edges.map((edge) => edge.relation_type) ?? [])),
    [graph],
  );

  // 노드 X의 선수개념 체인: X를 target으로 하는 PRECEDES 엣지의 source를 역방향 재귀 수집.
  function computePrecedeChain(startId: string) {
    const nodes = new Set<string>([startId]);
    const edges = new Set<string>();
    const queue = [startId];
    while (queue.length) {
      const cur = queue.pop()!;
      graph?.edges.forEach((edge, i) => {
        if (edge.relation_type === "PRECEDES" && edge.target === cur) {
          edges.add(`e${i}`);
          if (!nodes.has(edge.source)) {
            nodes.add(edge.source);
            queue.push(edge.source);
          }
        }
      });
    }
    return { nodes, edges };
  }

  // 노드 X + 직접 연결된 노드/엣지.
  function computeNeighborFocus(startId: string) {
    const nodes = new Set<string>([startId]);
    const edges = new Set<string>();
    graph?.edges.forEach((edge, i) => {
      if (edge.source === startId || edge.target === startId) {
        edges.add(`e${i}`);
        nodes.add(edge.source);
        nodes.add(edge.target);
      }
    });
    return { nodes, edges };
  }

  useEffect(() => {
    if (!graph || !containerRef.current) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements: [
        ...graph.nodes.map((node) => ({
          data: {
            id: node.id,
            label: node.name,
            type: node.type,
            definition: node.definition,
          },
        })),
        ...graph.edges.map((edge, i) => ({
          data: {
            id: `e${i}`,
            source: edge.source,
            target: edge.target,
            relationType: edge.relation_type,
            label: relationStyle(edge.relation_type).label,
          },
        })),
      ],
      style: [
        {
          selector: "node",
          style: {
            "background-color": "#4b5563",
            label: "data(label)",
            color: "#e5e7eb",
            "font-size": 10,
            "text-valign": "bottom",
            "text-halign": "center",
            "text-margin-y": 4,
            width: 24,
            height: 24,
          },
        },
        {
          selector: "node:selected",
          style: {
            "background-color": "#f59e0b",
            "border-width": 2,
            "border-color": "#fbbf24",
          },
        },
        {
          selector: "node.dimmed",
          style: { opacity: 0.15 },
        },
        {
          selector: "edge",
          style: {
            width: 1.5,
            "curve-style": "bezier",
            "target-arrow-shape": "triangle",
            "line-color": (ele) => relationStyle(ele.data("relationType")).color,
            "target-arrow-color": (ele) =>
              relationStyle(ele.data("relationType")).color,
            label: "data(label)",
            "font-size": 8,
            color: "#9ca3af",
            "text-rotation": "autorotate",
          },
        },
        {
          selector: "edge.dimmed",
          style: { opacity: 0.08 },
        },
      ],
      layout: buildLayoutOptions(layoutNameRef.current, densityRef.current),
      minZoom: 0.1,
      maxZoom: 3,
    });

    cy.on("tap", "node", (evt) => {
      const node = evt.target;
      const data = node.data();
      setSelected({ name: data.label, type: data.type, definition: data.definition });
      setHighlight(
        precedeModeRef.current
          ? computePrecedeChain(node.id())
          : computeNeighborFocus(node.id()),
      );
    });
    cy.on("mouseover", "node", (evt) => {
      const data = evt.target.data();
      setSelected({ name: data.label, type: data.type, definition: data.definition });
    });
    cy.on("tap", (evt) => {
      if (evt.target === cy) setHighlight(null);
    });

    cyRef.current = cy;
    return () => {
      cy.destroy();
      cyRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  // 관계타입 체크박스 → 해당 relation_type 엣지 표시/숨김.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.edges().forEach((edge) => {
      const hidden = hiddenTypes.has(edge.data("relationType"));
      edge.style("display", hidden ? "none" : "element");
    });
  }, [hiddenTypes, graph]);

  // 선수경로/이웃 포커스 하이라이트 → 나머지 흐리게.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.elements().removeClass("dimmed");
    if (!highlight) return;
    cy.nodes().forEach((node) => {
      if (!highlight.nodes.has(node.id())) node.addClass("dimmed");
    });
    cy.edges().forEach((edge) => {
      if (!highlight.edges.has(edge.id())) edge.addClass("dimmed");
    });
  }, [highlight, graph]);

  function toggleRelation(type: string) {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  function commitDensity(e: React.SyntheticEvent<HTMLInputElement>) {
    const value = Number(e.currentTarget.value) / 100;
    densityRef.current = value;
    if (layoutNameRef.current === "cose") {
      cyRef.current?.layout(buildLayoutOptions("cose", value)).run();
    }
  }

  function changeLayout(name: LayoutName) {
    setLayoutName(name);
    cyRef.current?.layout(buildLayoutOptions(name, densityRef.current)).run();
  }

  function togglePrecedeMode() {
    setPrecedeMode((v) => !v);
    setHighlight(null);
  }

  function runSearch() {
    const cy = cyRef.current;
    const query = searchQuery.trim().toLowerCase();
    if (!graph || !cy || !query) return;
    const match = graph.nodes.find((node) => node.name.toLowerCase().includes(query));
    if (!match) return;
    const ele = cy.getElementById(match.id);
    if (ele.empty()) return;
    cy.animate({ center: { eles: ele }, zoom: Math.max(cy.zoom(), 1.5) }, { duration: 300 });
    setSelected({ name: match.name, type: match.type, definition: match.definition });
    setHighlight({ nodes: new Set([match.id]), edges: new Set() });
  }

  function zoomBy(factor: number) {
    const cy = cyRef.current;
    if (!cy) return;
    cy.zoom({ level: cy.zoom() * factor, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-[13.5px] text-red-600">
        {error}
      </div>
    );
  }

  if (!graph) {
    return (
      <div className="flex h-full items-center justify-center text-[var(--muted-foreground)]">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full bg-[var(--background)]" />

      <div className="absolute left-4 right-44 top-4 flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)]/90 px-2 py-1.5 shadow-lg backdrop-blur-sm">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") runSearch();
          }}
          placeholder={t("Search concepts (Enter)")}
          className="min-w-0 flex-1 rounded border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-[13.5px] text-[var(--foreground)]"
        />
        <select
          value={layoutName}
          onChange={(e) => changeLayout(e.target.value as LayoutName)}
          className="rounded border border-[var(--border)] bg-[var(--background)] px-1 py-1 text-[13.5px] text-[var(--foreground)]"
        >
          <option value="cose">{t("Force")}</option>
          <option value="breadthfirst">{t("Hierarchical")}</option>
          <option value="concentric">{t("Concentric")}</option>
        </select>
        <button
          type="button"
          onClick={togglePrecedeMode}
          className={`shrink-0 whitespace-nowrap rounded px-2 py-1 text-[13.5px] ${
            precedeMode
              ? "bg-[var(--primary)] text-[var(--primary-foreground)]"
              : "border border-[var(--border)] text-[var(--foreground)]"
          }`}
        >
          {precedeMode ? t("Hide prerequisite path") : t("Show prerequisite path")}
        </button>
      </div>

      {selected && (
        <div className="absolute left-4 top-16 w-72 rounded-lg border border-[var(--border)] bg-[var(--card)] p-3 shadow-lg">
          <div className="text-[13.5px] font-semibold text-[var(--foreground)]">
            {selected.name}
          </div>
          <div className="mt-0.5 text-[13px] text-[var(--muted-foreground)]">
            {selected.type}
          </div>
          <p className="mt-2 text-[13.5px] leading-relaxed text-[var(--foreground)]">
            {selected.definition || t("No definition")}
          </p>
        </div>
      )}

      {relationTypes.length > 0 && (
        <div className="absolute right-4 top-4 w-40 rounded-lg border border-[var(--border)] bg-[var(--card)]/90 p-2 shadow-lg backdrop-blur-sm">
          <div className="mb-1 text-[13px] font-semibold text-[var(--foreground)]">
            {t("Relation types")}
          </div>
          <div className="space-y-1">
            {relationTypes.map((type) => {
              const style = relationStyle(type);
              return (
                <label
                  key={type}
                  className="flex cursor-pointer items-center gap-2 text-[13px] text-[var(--foreground)]"
                >
                  <input
                    type="checkbox"
                    checked={!hiddenTypes.has(type)}
                    onChange={() => toggleRelation(type)}
                    className="h-3 w-3"
                  />
                  <span
                    className="inline-block h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: style.color }}
                  />
                  <span>{t(style.label)}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)]/90 px-3 py-2 text-[13px] text-[var(--foreground)] shadow-lg backdrop-blur-sm">
        <span>{t("Sparse")}</span>
        <input
          type="range"
          min={0}
          max={100}
          value={densityDisplay}
          onChange={(e) => setDensityDisplay(Number(e.target.value))}
          onMouseUp={commitDensity}
          onTouchEnd={commitDensity}
          className="h-1 w-32 accent-current"
        />
        <span>{t("Dense")}</span>
      </div>

      <div className="absolute bottom-4 right-4 flex flex-col gap-1 rounded-lg border border-[var(--border)] bg-[var(--card)]/90 p-1 shadow-lg backdrop-blur-sm">
        <button
          type="button"
          onClick={() => zoomBy(1.2)}
          className="h-6 w-6 rounded text-[13.5px] text-[var(--foreground)] hover:bg-[var(--muted)]"
        >
          +
        </button>
        <button
          type="button"
          onClick={() => cyRef.current?.fit(undefined, 30)}
          className="rounded px-1 text-[13px] text-[var(--foreground)] hover:bg-[var(--muted)]"
        >
          {t("Fit")}
        </button>
        <button
          type="button"
          onClick={() => zoomBy(1 / 1.2)}
          className="h-6 w-6 rounded text-[13.5px] text-[var(--foreground)] hover:bg-[var(--muted)]"
        >
          −
        </button>
      </div>
    </div>
  );
}
